import { Component, ElementRef, ViewChild, signal, OnInit, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface CropArea {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

@Component({
  selector: 'app-pdf-to-image',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pdf-to-image.component.html',
  styleUrl: './pdf-to-image.component.css'
})
export class PdfToImageComponent {
  title = 'pdf-cropper-angular';

  @Output() classApplied = new EventEmitter<boolean>();
  @Output() saveConfirmationEvent = new EventEmitter<string>();

  private currentRenderTask: any = null;

  @ViewChild('pdfCanvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasContainer', { static: false }) containerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput', { static: false }) fileInputRef!: ElementRef<HTMLInputElement>;

  // Signals for reactive state management
  pdfDocument = signal<any>(null);
  currentPage = signal(1);
  totalPages = signal(0);
  zoomLevel = signal(1);
  cropMode = signal(false);
  cropArea = signal<CropArea | null>(null);
  isDragging = signal(false);
  isLoading = signal(false);

  // Preview modal signals
  showPreview = signal(false);
  previewImageData = signal<string>('');
  rotationAngle = signal(0);

  // Private properties
  private pdfjsLib: any;
  private isDrawing = false;
  private startX = 0;
  private startY = 0;
  private lastX = 0;
  private lastY = 0;

  // Math reference for template
  Math = Math;

  async ngOnInit() {
    await this.loadPDFJS();
  }

  /*private async loadPDFJS(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).pdfjsLib) {
        this.pdfjsLib = (window as any).pdfjsLib;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        this.pdfjsLib = (window as any).pdfjsLib;
        this.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load PDF.js'));
      document.head.appendChild(script);
    });
  }*/
  private async loadPDFJS(): Promise<void> {
    if (typeof window === 'undefined') return;
    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && (window as any).pdfjsLib) {
        this.pdfjsLib = (window as any).pdfjsLib;
        resolve();
        return;
      }

      if (typeof window !== 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          this.pdfjsLib = (window as any).pdfjsLib;
          this.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          resolve();
        };
        script.onerror = () => reject(new Error('Failed to load PDF.js'));
        document.head.appendChild(script);
      } else {
        reject(new Error('window is not defined'));
      }
    });
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.isLoading.set(true);
      const file = input.files[0];

      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await this.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        this.pdfDocument.set(pdf);
        this.totalPages.set(pdf.numPages);
        this.currentPage.set(1);
        this.cropArea.set(null);
        this.cropMode.set(false);

        setTimeout(() => {
          this.renderPage();
          this.isLoading.set(false);
        }, 100);
      } catch (error) {
        console.error('Error loading PDF:', error);
        alert('Error loading PDF file. Please make sure it\'s a valid PDF.');
        this.isLoading.set(false);
      }
    }
  }

  loadNewPDF(): void {
    if (!this.fileInputRef) {
      console.error('fileInputRef is not initialized');
      return;
    }
    this.fileInputRef.nativeElement.click();
  }


  async renderPage(): Promise<void> {
    if (!this.pdfDocument() || !this.canvasRef) return;

    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d')!;


    const page = await this.pdfDocument().getPage(this.currentPage());
    const viewport = page.getViewport({ scale: this.zoomLevel() });

    // Set canvas dimensions based on viewport
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Cancel any ongoing render task
    if (this.currentRenderTask) {
      try {
        this.currentRenderTask.cancel();
      } catch (err) {
        console.warn('Error cancelling previous render task', err);
      }
    }

    // Clear canvas
    //ctx.clearRect(0, 0, canvas.width, canvas.height);

    const renderContext = {
      canvasContext: ctx,
      viewport: viewport,
    };

    try {
      this.currentRenderTask = page.render(renderContext);
      await this.currentRenderTask.promise;
      this.currentRenderTask = null;
    } catch (err) {
      if ((err as any)?.name === 'RenderingCancelledException') {
        console.log('Previous render cancelled');
      } else {
        console.error('Error rendering page:', err);
      }
    }
  }

  /*async renderPage(): Promise<void> {
      if (!this.pdfDocument() || !this.canvasRef || !this.containerRef) return;
  
      const canvas = this.canvasRef.nativeElement;
      const ctx = canvas.getContext('2d')!;
      const container = this.containerRef.nativeElement;
  
      try {
        const page = await this.pdfDocument().getPage(this.currentPage());
        const viewport = page.getViewport({ scale: this.zoomLevel() });
  
        // Set canvas dimensions
        canvas.width = viewport.width;
        canvas.height = viewport.height;
  
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
  
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        };
  
        await page.render(renderContext).promise;
  
        // Center the container on initial load
        container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
        container.scrollTop = (container.scrollHeight - container.clientHeight) / 2;
  
        // Reset crop area when page changes or zoom changes
        this.cropArea.set(null);
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    }*/

  /*async renderPage(): Promise<void> {
    if (!this.pdfDocument() || !this.canvasRef || !this.containerRef) return;

    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d')!;
    const container = this.containerRef.nativeElement;

    try {
      const page = await this.pdfDocument().getPage(this.currentPage());
      const viewport = page.getViewport({ scale: this.zoomLevel() });

      // Set canvas dimensions based on viewport
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const renderContext = {
        canvasContext: ctx,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Adjust container dimensions to match canvas size
      container.style.width = `${canvas.width}px`;
      container.style.height = `${canvas.height}px`;

      // Center the container on initial load
      container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
      container.scrollTop = (container.scrollHeight - container.clientHeight) / 2;

      // Reset crop area when page changes or zoom changes
      this.cropArea.set(null);

      console.log('Canvas dimensions:', canvas.width, canvas.height);
      console.log('Container dimensions:', container.style.width, container.style.height);
    } catch (error) {
      console.error('Error rendering page:', error);
    }
  }*/

  // Zoom controls
  zoomIn(): void {
    this.zoomLevel.set(Math.min(this.zoomLevel() * 1.25, 5));
    this.renderPage();
  }

  zoomOut(): void {
    this.zoomLevel.set(Math.max(this.zoomLevel() / 1.25, 0.1));
    this.renderPage();
  }

  resetZoom(): void {
    this.zoomLevel.set(1);
    this.renderPage();
  }

  // Page navigation
  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.set(this.currentPage() - 1);
      this.renderPage();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.set(this.currentPage() + 1);
      this.renderPage();
    }
  }

  // Crop functionality
  toggleCropMode(): void {
    this.cropMode.set(!this.cropMode());
    if (!this.cropMode()) {
      this.cropArea.set(null);
    }
  }

  getCursor(): string {
    if (this.cropMode()) {
      return 'crosshair';
    }
    return this.isDragging() ? 'grabbing' : 'grab';
  }

  // Helper methods for crop overlay positioning
  // 3. Fix crop overlay positioning - Update helper methods
  // Update helper methods to work with display coordinates
  getCropLeft(): number {
    const crop = this.cropArea();
    return crop ? Math.min(crop.startX, crop.endX) : 0;
  }

  getCropTop(): number {
    const crop = this.cropArea();
    return crop ? Math.min(crop.startY, crop.endY) : 0;
  }

  getCropWidth(): number {
    const crop = this.cropArea();
    return crop ? Math.abs(crop.endX - crop.startX) : 0;
  }

  getCropHeight(): number {
    const crop = this.cropArea();
    return crop ? Math.abs(crop.endY - crop.startY) : 0;
  }



  onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    // Get mouse position relative to canvas
    const x = (event.clientX - rect.left) / this.zoomLevel();
    const y = (event.clientY - rect.top) / this.zoomLevel();

    if (this.cropMode()) {
      this.isDrawing = true;
      this.startX = x;
      this.startY = y;

      // Initialize crop area
      this.cropArea.set({
        startX: x,
        startY: y,
        endX: x,
        endY: y
      });
    } else if (this.cropArea() && this.isInsideCropArea(x, y)) {
      // Start dragging the crop area
      this.isDragging.set(true);
      this.lastX = x;
      this.lastY = y;
    }
  }

  isInsideCropArea(x: number, y: number): boolean {
    const crop = this.cropArea();
    if (!crop) return false;

    const left = Math.min(crop.startX, crop.endX);
    const right = Math.max(crop.startX, crop.endX);
    const top = Math.min(crop.startY, crop.endY);
    const bottom = Math.max(crop.startY, crop.endY);

    return x >= left && x <= right && y >= top && y <= bottom;
  }


  /*
   
    onMouseDown(event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();
   
      const canvas = this.canvasRef.nativeElement;
      const rect = canvas.getBoundingClientRect();
   
      // Get mouse position relative to canvas
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
   
      // Clamp coordinates to canvas bounds
      const clampedX = Math.max(0, Math.min(x, rect.width));
      const clampedY = Math.max(0, Math.min(y, rect.height));
   
      console.log('Mouse down at:', clampedX, clampedY, 'Canvas rect:', rect);
   
      if (this.cropMode()) {
        this.isDrawing = true;
        this.startX = clampedX;
        this.startY = clampedY;
   
        // Initialize crop area
        this.cropArea.set({
          startX: clampedX,
          startY: clampedY,
          endX: clampedX,
          endY: clampedY
        });
   
        console.log('Crop started:', this.cropArea());
      } else {
        // Pan mode
        this.isDragging.set(true);
        this.lastX = event.clientX;
        this.lastY = event.clientY;
      }
    }*/


  /*// 2. Fix crop area selection - Update onMouseMove method
  onMouseMove(event: MouseEvent): void {
    if (this.cropMode() && this.isDrawing) {
      event.preventDefault();
      event.stopPropagation();
  
      const canvas = this.canvasRef.nativeElement;
      const rect = canvas.getBoundingClientRect();
  
      // Get current mouse position
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
  
      // Clamp to canvas bounds
      const clampedX = Math.max(0, Math.min(x, rect.width));
      const clampedY = Math.max(0, Math.min(y, rect.height));
  
      // Update crop area
      this.cropArea.set({
        startX: this.startX,
        startY: this.startY,
        endX: clampedX,
        endY: clampedY
      });
  
      console.log('Crop area updated:', this.cropArea());
  
    } else if (this.isDragging() && !this.cropMode()) {
      // Pan mode
      const deltaX = event.clientX - this.lastX;
      const deltaY = event.clientY - this.lastY;
  
      const container = this.containerRef.nativeElement;
      container.scrollLeft -= deltaX;
      container.scrollTop -= deltaY;
  
      this.lastX = event.clientX;
      this.lastY = event.clientY;
    }
  }*/

  onMouseMove(event: MouseEvent): void {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    const x = (event.clientX - rect.left) / this.zoomLevel();
    const y = (event.clientY - rect.top) / this.zoomLevel();

    if (this.cropMode() && this.isDrawing) {
      event.preventDefault();
      event.stopPropagation();

      // Update crop area
      this.cropArea.set({
        startX: this.startX,
        startY: this.startY,
        endX: x,
        endY: y,
      });
    } else if (this.isDragging() && !this.cropMode()) {
      // Pan mode
      const deltaX = event.clientX - this.lastX;
      const deltaY = event.clientY - this.lastY;

      const container = this.containerRef.nativeElement;
      container.scrollLeft -= deltaX;
      container.scrollTop -= deltaY;

      this.lastX = event.clientX;
      this.lastY = event.clientY;
    }
  }


  /*onMouseUp(): void {
    if (this.isDrawing && this.cropMode()) {
      const crop = this.cropArea();
      if (crop) {
        const width = Math.abs(crop.endX - crop.startX);
        const height = Math.abs(crop.endY - crop.startY);
  
        console.log('Crop finished. Width:', width, 'Height:', height);
  
        // Clear if too small
        if (width < 10 || height < 10) {
          this.cropArea.set(null);
          console.log('Crop too small, cleared');
        }
      }
    }
  
    this.isDrawing = false;
    this.isDragging.set(false);
  }*/


  onMouseUp(): void {
    if (this.isDrawing && this.cropMode()) {
      const crop = this.cropArea();
      if (crop) {
        const width = Math.abs(crop.endX - crop.startX);
        const height = Math.abs(crop.endY - crop.startY);

        // Clear if too small
        if (width < 10 || height < 10) {
          this.cropArea.set(null);
        }
      }
    }

    this.isDrawing = false;
    this.isDragging.set(false);
  }


  // 5. Enhanced wheel zoom with zoom-to-point
  /* onWheel(event: WheelEvent): void {
     event.preventDefault();
 
     const rect = this.canvasRef.nativeElement.getBoundingClientRect();
     const centerX = event.clientX - rect.left;
     const centerY = event.clientY - rect.top;
 
     const delta = event.deltaY;
     const zoomFactor = delta < 0 ? 1.25 : 1 / 1.25;
     const newZoom = this.zoomLevel() * zoomFactor;
 
     this.zoomToArea(centerX, centerY, newZoom);
   }*/

  onWheel(event: WheelEvent): void {
    event.preventDefault();

    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;

    const delta = event.deltaY;
    const zoomFactor = delta < 0 ? 1.25 : 1 / 1.25;
    const newZoom = this.zoomLevel() * zoomFactor;

    this.zoomToArea(centerX, centerY, newZoom);
  }


  async exportCrop(): Promise<void> {
    if (!this.cropArea() || !this.pdfDocument()) return;

    this.isLoading.set(true);

    try {
      const crop = this.cropArea()!;
      const canvas = this.canvasRef.nativeElement;

      // Adjust crop area coordinates based on zoom level
      const cropX = Math.min(crop.startX, crop.endX) * this.zoomLevel();
      const cropY = Math.min(crop.startY, crop.endY) * this.zoomLevel();
      const cropWidth = Math.abs(crop.endX - crop.startX) * this.zoomLevel();
      const cropHeight = Math.abs(crop.endY - crop.startY) * this.zoomLevel();

      console.log('Export crop:', { cropX, cropY, cropWidth, cropHeight });

      if (cropWidth < 10 || cropHeight < 10) {
        alert('Crop area too small. Please select a larger area.');
        this.isLoading.set(false);
        return;
      }

      // Create high-resolution canvas for export
      const exportCanvas = document.createElement('canvas');
      const exportCtx = exportCanvas.getContext('2d')!;

      const highResScale = 4; // Adjust for high-resolution export
      const page = await this.pdfDocument().getPage(this.currentPage());
      const viewport = page.getViewport({ scale: this.zoomLevel() * highResScale });

      exportCanvas.width = viewport.width;
      exportCanvas.height = viewport.height;

      // Render page at high resolution
      await page.render({
        canvasContext: exportCtx,
        viewport: viewport,
      }).promise;

      // Create final crop canvas
      const finalCanvas = document.createElement('canvas');
      const finalCtx = finalCanvas.getContext('2d')!;

      const finalCropWidth = cropWidth * highResScale;
      const finalCropHeight = cropHeight * highResScale;

      finalCanvas.width = finalCropWidth;
      finalCanvas.height = finalCropHeight;

      // Extract cropped area
      finalCtx.drawImage(
        exportCanvas,
        cropX * highResScale,
        cropY * highResScale,
        finalCropWidth,
        finalCropHeight,
        0,
        0,
        finalCropWidth,
        finalCropHeight
      );

      // Convert cropped image to base64
      const croppedImageBase64 = finalCanvas.toDataURL('image/png');

      // Show preview modal instead of immediate upload/download
      this.previewImageData.set(croppedImageBase64);
      this.rotationAngle.set(0);
      this.showPreview.set(true);
      this.isLoading.set(false);

    } catch (error) {
      console.error('Error exporting crop:', error);
      alert('Error exporting cropped image');
      this.isLoading.set(false);
    }
  }

  /*async exportCrop(): Promise<void> {
    if (!this.cropArea() || !this.pdfDocument()) return;

    this.isLoading.set(true);

    try {
      const crop = this.cropArea()!;
      const canvas = this.canvasRef.nativeElement;
      const rect = canvas.getBoundingClientRect();

      // Convert display coordinates to actual canvas coordinates
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const cropX = Math.min(crop.startX, crop.endX) * scaleX;
      const cropY = Math.min(crop.startY, crop.endY) * scaleY;
      const cropWidth = Math.abs(crop.endX - crop.startX) * scaleX;
      const cropHeight = Math.abs(crop.endY - crop.startY) * scaleY;

      console.log('Export crop:', { cropX, cropY, cropWidth, cropHeight });

      if (cropWidth < 10 || cropHeight < 10) {
        alert('Crop area too small. Please select a larger area.');
        this.isLoading.set(false);
        return;
      }

      // Create high-resolution canvas for export
      const exportCanvas = document.createElement('canvas');
      const exportCtx = exportCanvas.getContext('2d')!;

      const highResScale = 4;
      const page = await this.pdfDocument().getPage(this.currentPage());
      const viewport = page.getViewport({ scale: this.zoomLevel() * highResScale });

      exportCanvas.width = viewport.width;
      exportCanvas.height = viewport.height;

      // Render page at high resolution
      await page.render({
        canvasContext: exportCtx,
        viewport: viewport
      }).promise;

      // Create final crop canvas
      const finalCanvas = document.createElement('canvas');
      const finalCtx = finalCanvas.getContext('2d')!;

      const finalCropWidth = cropWidth * highResScale;
      const finalCropHeight = cropHeight * highResScale;

      finalCanvas.width = finalCropWidth;
      finalCanvas.height = finalCropHeight;

      // Extract cropped area
      finalCtx.drawImage(
        exportCanvas,
        cropX * highResScale,
        cropY * highResScale,
        finalCropWidth,
        finalCropHeight,
        0,
        0,
        finalCropWidth,
        finalCropHeight
      );

      // Download
      finalCanvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pdf-crop-page-${this.currentPage()}-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        this.isLoading.set(false);
      }, 'image/png', 1.0);

    } catch (error) {
      console.error('Error exporting crop:', error);
      alert('Error exporting cropped image');
      this.isLoading.set(false);
    }
  }*/

  // 4. Add zoom to specific area functionality
  /* zoomToArea(centerX: number, centerY: number, newZoom: number): void {
     const container = this.containerRef.nativeElement;
     const oldZoom = this.zoomLevel();
 
     // Calculate the point in the container before zoom
     const oldScrollLeft = container.scrollLeft;
     const oldScrollTop = container.scrollTop;
     const containerRect = container.getBoundingClientRect();
 
     // Set new zoom
     this.zoomLevel.set(Math.max(0.1, Math.min(newZoom, 5)));
 
     // Re-render at new zoom
     setTimeout(() => {
       this.renderPage();
 
       // Calculate new scroll position to keep the point centered
       const zoomRatio = this.zoomLevel() / oldZoom;
       const newScrollLeft = (oldScrollLeft + centerX) * zoomRatio - centerX;
       const newScrollTop = (oldScrollTop + centerY) * zoomRatio - centerY;
 
       container.scrollLeft = newScrollLeft;
       container.scrollTop = newScrollTop;
     }, 50);
   }*/

  zoomToArea(centerX: number, centerY: number, newZoom: number): void {
    const container = this.containerRef.nativeElement;
    const oldZoom = this.zoomLevel();

    // Calculate the point in the container before zoom
    const oldScrollLeft = container.scrollLeft;
    const oldScrollTop = container.scrollTop;

    // Set new zoom
    this.zoomLevel.set(Math.max(0.1, Math.min(newZoom, 5)));

    // Re-render at new zoom
    setTimeout(() => {
      this.renderPage();

      // Calculate new scroll position to keep the point centered
      const zoomRatio = this.zoomLevel() / oldZoom;
      const newScrollLeft = (oldScrollLeft + centerX) * zoomRatio - centerX;
      const newScrollTop = (oldScrollTop + centerY) * zoomRatio - centerY;

      container.scrollLeft = newScrollLeft;
      container.scrollTop = newScrollTop;
    }, 50);
  }


  // 6. Add double-click to zoom functionality (add this to your component)
  onDoubleClick(event: MouseEvent): void {
    event.preventDefault();

    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;

    // Zoom in to 2x or reset to 1x if already zoomed
    const newZoom = this.zoomLevel() > 1.5 ? 1 : 2;
    this.zoomToArea(centerX, centerY, newZoom);
  }

  toggleFullScreen(): void {
    const container = this.containerRef.nativeElement;
    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => {
        container.style.width = `${container.scrollWidth}px`;
        container.style.height = `${container.scrollHeight}px`;
      });
    } else {
      document.exitFullscreen().then(() => {
        container.style.width = '100%';
        container.style.height = '100%';
      });
    }
  }

  public toggleClass(): void {
    this.classApplied.emit(false);
  }

  // Rotate image by 90 degrees clockwise
  rotateImage(): void {
    const currentAngle = this.rotationAngle();
    const newAngle = (currentAngle + 90) % 360;
    this.rotationAngle.set(newAngle);
  }

  // Re-capture functionality
  reCapture(): void {
    // Close preview modal
    this.showPreview.set(false);
    this.previewImageData.set('');
    this.rotationAngle.set(0);

    // Return to crop mode with the PDF visible
    this.cropMode.set(true);
    this.cropArea.set(null);
  }

  // Close preview modal
  closePreview(): void {
    this.showPreview.set(false);
    this.previewImageData.set('');
    this.rotationAngle.set(0);
  }

  // Finalize image (upload/select or download)
  async finalizeImage(isDownload: boolean = false): Promise<void> {
    if (!this.previewImageData()) return;

    this.isLoading.set(true);

    try {
      const rotatedImageData = await this.getRotatedImageData();

      if (isDownload) {
        // Download the image
        const a = document.createElement('a');
        a.href = rotatedImageData;
        a.download = `pdf-crop-page-${this.currentPage()}-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.isLoading.set(false);
      } else {
        // Emit the rotated image for upload
        this.saveConfirmationEvent.emit(rotatedImageData);

        // Close preview and main dialog
        this.showPreview.set(false);
        this.previewImageData.set('');
        this.rotationAngle.set(0);
        this.toggleClass();
        this.isLoading.set(false);
      }
    } catch (error) {
      console.error('Error finalizing image:', error);
      alert('Error processing image');
      this.isLoading.set(false);
    }
  }

  // Get rotated image data
  private async getRotatedImageData(): Promise<string> {
    const img = new Image();
    img.src = this.previewImageData();

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const angle = this.rotationAngle();

        // Adjust canvas dimensions based on rotation
        if (angle === 90 || angle === 270) {
          canvas.width = img.height;
          canvas.height = img.width;
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }

        // Translate and rotate
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);

        // Draw image centered
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        resolve(canvas.toDataURL('image/png', 1.0));
      };

      img.onerror = () => reject(new Error('Failed to load image'));
    });
  }
}